import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, FileText } from 'lucide-react';
import { Seo } from '../components/Seo';
import { SITE, breadcrumbJsonLd } from '../data/seoData';
import { SALES_RESOURCE_CATEGORIES } from '../data/salesResourcesData';

export default function SalesResources() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Marketing and Sales Resources',
      url: `${SITE.url}/sales-resources`,
      description:
        'Downloadable sales collateral, branding highlights, product catalogs, application instructions and video tutorials from Evergreen Graphics Group.',
    },
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Sales Resources', path: '/sales-resources' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="sales-resources-page">
      <Seo
        title="Marketing and Sales Resources — Downloads & Tutorials | Evergreen Graphics Group"
        description="Free downloadable sales collateral, branding highlights, product catalogs, application guides and video tutorials for custom stickers, labels, decals and transfers."
        path="/sales-resources"
        jsonLd={jsonLd}
      />

      <section className="bg-[#F4F7F2]">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#4A7C59] mb-3">Download Centre</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#14281D]">
            Marketing and Sales Resources
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#1F3D2C]/80 max-w-2xl">
            Flyers, catalogs, application guides and tutorials — everything you need to sell,
            present and apply our products. Click any category to browse and download.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SALES_RESOURCE_CATEGORIES.map((cat) => {
            const cover = cat.items.find((i) => i.thumb)?.thumb;
            const videoCount = cat.items.filter((i) => i.type === 'video').length;
            const pdfCount = cat.items.length - videoCount;
            return (
              <Link
                key={cat.slug}
                to={`/sales-resources/${cat.slug}`}
                data-testid={`sales-resources-cat-${cat.slug}`}
                className="group bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#6FBF73] hover:shadow-xl transition-all overflow-hidden flex flex-col"
              >
                <div className="h-44 bg-[#E8F1E6] overflow-hidden flex items-center justify-center">
                  {cover ? (
                    <img
                      src={cover}
                      alt={`${cat.name} downloads preview — Evergreen Graphics Group marketing resources`}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <PlayCircle className="w-16 h-16 text-[#4A7C59] group-hover:scale-110 transition-transform duration-300" />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="text-base md:text-lg font-bold text-[#14281D] group-hover:text-[#2F5D3A] transition-colors">
                    {cat.name}
                  </h2>
                  <p className="mt-1.5 text-sm text-[#1F3D2C]/70 leading-relaxed flex-1">{cat.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-[#4A7C59]">
                    <span className="flex items-center gap-1.5">
                      {videoCount > 0 ? <PlayCircle className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                      {pdfCount > 0 && `${pdfCount} PDF${pdfCount === 1 ? '' : 's'}`}
                      {pdfCount > 0 && videoCount > 0 && ' · '}
                      {videoCount > 0 && `${videoCount} video${videoCount === 1 ? '' : 's'}`}
                    </span>
                    <span className="font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Browse <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="bg-[#14281D] text-white py-10 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 {SITE.name}. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
          </p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: {SITE.phone} | Email: {SITE.email}</p>
        </div>
      </footer>
    </div>
  );
}
